import { Customer, Lead, UserActivity } from '../types/user';
import { Booking } from '../types/booking';
import { analytics } from '../utils/analytics';

// Frontend database service that communicates with backend API
// Currently using localStorage for development when backend is not available
class DatabaseService {
    private apiBaseUrl: string;
    private useLocalStorage: boolean; constructor() {
        this.apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '/api';
        // Use localStorage ONLY if explicitly enabled via env var
        // When REACT_APP_API_BASE_URL is not set, we use '/api' which proxies to backend via Vite
        this.useLocalStorage = process.env.REACT_APP_USE_LOCAL_STORAGE === 'true';

        console.log('🔍 DatabaseService initialized:', {
            apiBaseUrl: this.apiBaseUrl,
            useLocalStorage: this.useLocalStorage,
            hasEnvVar: !!process.env.REACT_APP_API_BASE_URL
        });
    }

    private async makeRequest(endpoint: string, options: RequestInit = {}): Promise<any> {
        // Use localStorage for development when backend is not available
        if (this.useLocalStorage) {
            console.log(`🔍 Using localStorage for ${endpoint}`);
            return this.handleLocalStorageRequest(endpoint, options);
        }

        const url = `${this.apiBaseUrl}${endpoint}`;
        console.log(`🔍 Making API request to: ${url}`, options.method || 'GET');

        try {
            const response = await fetch(url, {
                headers: {
                    'Content-Type': 'application/json',
                    ...options.headers,
                },
                ...options,
            });

            console.log(`🔍 API response from ${url}: status=${response.status}`);

            if (!response.ok) {
                const errorText = await response.text();
                console.error(`❌ HTTP error for ${endpoint}! status: ${response.status}, body:`, errorText);
                throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
            }

            const jsonData = await response.json();
            console.log(`✅ API request successful for ${endpoint}`);
            return jsonData;
        } catch (error) {
            console.error(`❌ API request failed for ${endpoint}:`, error);
            analytics.trackError('api_request_failed', `${endpoint}: ${error instanceof Error ? error.message : 'Unknown error'}`);

            // CRITICAL: Do not fall back to localStorage for booking creation - we need real database records
            if (endpoint === '/bookings' && options.method === 'POST') {
                console.error('❌ CRITICAL: Booking creation failed, NOT using localStorage fallback');
                throw error;
            }

            // Fallback to localStorage for development (non-critical endpoints only)
            console.log(`⚠️ Falling back to localStorage for ${endpoint}`);
            return this.handleLocalStorageRequest(endpoint, options);
        }
    }

    private getMockResponse(endpoint: string): any {
        // Return mock data for development when backend is not available
        if (endpoint.includes('customers')) {
            return {
                id: 'mock-customer-id',
                firstName: 'Test',
                lastName: 'Customer',
                email: 'test@example.com',
                phone: '0400000000',
                address: '123 Test St',
                suburb: 'Test Suburb',
                postcode: '1234',
                state: 'NSW',
                createdAt: new Date(),
                updatedAt: new Date(),
            };
        }

        if (endpoint.includes('leads')) {
            return {
                id: 'mock-lead-id',
                customerId: 'mock-customer-id',
                source: 'website',
                status: 'new',
                createdAt: new Date(),
                updatedAt: new Date(),
            };
        }

        if (endpoint.includes('bookings')) {
            return {
                id: 'mock-booking-id',
                customerId: 'mock-customer-id',
                status: 'pending',
                createdAt: new Date(),
                updatedAt: new Date(),
            };
        }

        return { success: true };
    }

    /**
     * Initialize database tables (handled by backend)
     */
    async initializeTables(): Promise<void> {
        try {
            await this.makeRequest('/admin/initialize', {
                method: 'POST',
            });
            console.log('Database tables initialized');
            analytics.track('database_tables_initialized', 'database_service');
        } catch (error) {
            console.warn('Database initialization skipped (backend not available)');
            analytics.track('database_initialization_skipped', 'database_service');
        }
    }

    /**
     * Create or update customer
     */
    async upsertCustomer(customer: Omit<Customer, 'id' | 'createdAt' | 'updatedAt'>, sessionId?: string): Promise<Customer> {
        const response = await this.makeRequest('/customers', {
            method: 'POST',
            body: JSON.stringify(customer),
        });

        const customerData = {
            id: response.id,
            firstName: response.firstName || response.first_name,
            lastName: response.lastName || response.last_name,
            email: response.email,
            phone: response.phone,
            address: response.address,
            suburb: response.suburb,
            postcode: response.postcode,
            state: response.state,
            createdAt: new Date(response.createdAt || response.created_at),
            updatedAt: new Date(response.updatedAt || response.updated_at),
        };

        // Link customer to session activities if sessionId is provided
        if (sessionId && customerData.id) {
            try {
                await this.linkCustomerToSession(customerData.id, sessionId);
                console.log(`✅ Successfully linked customer ${customerData.id} to session ${sessionId}`);
            } catch (error) {
                console.error('Failed to link customer to session:', error);
                // Don't fail the customer creation if session linking fails
            }
        }

        return customerData;
    }

    /**
     * Link customer to all activities in a session
     */
    async linkCustomerToSession(customerId: string, sessionId: string): Promise<void> {
        try {
            await this.makeRequest(`/sessions/${sessionId}/link-customer`, {
                method: 'PATCH',
                body: JSON.stringify({ customerId }),
            });
        } catch (error) {
            console.error('Error linking customer to session:', error);
            throw error;
        }
    }

    /**
     * Create lead
     */
    async createLead(lead: Omit<Lead, 'id' | 'createdAt' | 'updatedAt'>): Promise<Lead> {
        const response = await this.makeRequest('/leads', {
            method: 'POST',
            body: JSON.stringify(lead),
        });

        return {
            id: response.id,
            customerId: response.customerId || response.customer_id,
            sessionId: response.sessionId || response.session_id,
            source: response.source,
            medium: response.medium,
            campaign: response.campaign,
            utmSource: response.utmSource || response.utm_source,
            utmMedium: response.utmMedium || response.utm_medium,
            utmCampaign: response.utmCampaign || response.utm_campaign,
            utmTerm: response.utmTerm || response.utm_term,
            utmContent: response.utmContent || response.utm_content,
            referrer: response.referrer,
            landingPage: response.landingPage || response.landing_page,
            userAgent: response.userAgent || response.user_agent,
            ipAddress: response.ipAddress || response.ip_address,
            status: response.status,
            stage: response.stage || 'contact',
            data: response.data || {},
            createdAt: new Date(response.createdAt || response.created_at),
            updatedAt: new Date(response.updatedAt || response.updated_at),
        };
    }

    /**
     * Track user activity
     */
    async trackActivity(activity: Omit<UserActivity, 'id' | 'timestamp'>): Promise<UserActivity> {
        const response = await this.makeRequest('/activities', {
            method: 'POST',
            body: JSON.stringify(activity),
        });

        return {
            id: response.id,
            sessionId: response.sessionId || response.session_id,
            customerId: response.customerId || response.customer_id,
            action: response.action || response.eventType || response.event_type || 'unknown',
            page: response.page || response.pageUrl || response.page_url || 'unknown',
            eventType: response.eventType || response.event_type,
            eventData: response.eventData || response.event_data,
            pageUrl: response.pageUrl || response.page_url,
            ipAddress: response.ipAddress || response.ip_address,
            userAgent: response.userAgent || response.user_agent,
            data: response.data || response.eventData || response.event_data,
            timestamp: new Date(response.timestamp),
        };
    }

    /**
     * Create booking
     */
    async createBooking(booking: Omit<Booking, 'id' | 'createdAt' | 'updatedAt'>): Promise<Booking> {
        const response = await this.makeRequest('/bookings', {
            method: 'POST',
            body: JSON.stringify(booking),
        });

        console.log('🔍 DEBUG createBooking - Backend response:', JSON.stringify(response, null, 2));
        console.log('🔍 DEBUG createBooking - response.id:', response.id, 'type:', typeof response.id);
        console.log('🔍 DEBUG createBooking - response.timeSlot:', JSON.stringify(response.timeSlot, null, 2));

        return {
            id: response.id,
            customerId: response.customerId || response.customer_id,
            staffId: response.staffId || response.staff_id,
            serviceType: response.serviceType || response.service_type,
            date: response.date || response.scheduled_date,
            timeSlot: {
                id: response.timeSlot?.id || response.id,
                startTime: response.timeSlot?.startTime || response.time_slot_start,
                endTime: response.timeSlot?.endTime || response.time_slot_end,
                available: false,
                staffId: response.staffId || response.staff_id,
            },
            status: response.status,
            notes: response.notes,
            measurements: response.measurements,
            estimatedPrice: response.estimatedPrice || response.estimated_price,
            promoCode: response.promoCode || response.promo_code,
            createdAt: new Date(response.createdAt || response.created_at),
            updatedAt: new Date(response.updatedAt || response.updated_at),
        };
    }

    /**
     * Update booking with TRAK booking ID
     */
    async updateBookingWithTrakId(bookingId: string, trakBookingId: string): Promise<void> {
        await this.makeRequest(`/bookings/${bookingId}`, {
            method: 'PATCH',
            body: JSON.stringify({ trak_job_id: trakBookingId }),
        });
    }

    /**
     * Get customer by email
     */
    async getCustomerByEmail(email: string): Promise<Customer | null> {
        try {
            const response = await this.makeRequest(`/customers?email=${encodeURIComponent(email)}`);

            if (!response || response.length === 0) {
                return null;
            }

            const customer = Array.isArray(response) ? response[0] : response;

            return {
                id: customer.id,
                firstName: customer.firstName || customer.first_name,
                lastName: customer.lastName || customer.last_name,
                email: customer.email,
                phone: customer.phone,
                address: customer.address,
                suburb: customer.suburb,
                postcode: customer.postcode,
                state: customer.state,
                createdAt: new Date(customer.createdAt || customer.created_at),
                updatedAt: new Date(customer.updatedAt || customer.updated_at),
            };
        } catch (error) {
            return null;
        }
    }

    /**
     * Get booking by ID
     */
    async getBookingById(bookingId: string): Promise<Booking | null> {
        try {
            const response = await this.makeRequest(`/bookings/${bookingId}`);

            if (!response) {
                return null;
            }

            return {
                id: response.id,
                customerId: response.customerId || response.customer_id,
                staffId: response.staffId || response.staff_id,
                serviceType: response.serviceType || response.service_type,
                date: response.date || response.scheduled_date,
                timeSlot: {
                    id: response.timeSlot?.id || response.id,
                    startTime: response.timeSlot?.startTime || response.time_slot_start,
                    endTime: response.timeSlot?.endTime || response.time_slot_end,
                    available: false,
                    staffId: response.staffId || response.staff_id,
                },
                status: response.status,
                notes: response.notes,
                measurements: response.measurements,
                estimatedPrice: response.estimatedPrice || response.estimated_price,
                promoCode: response.promoCode || response.promo_code,
                createdAt: new Date(response.createdAt || response.created_at),
                updatedAt: new Date(response.updatedAt || response.updated_at),
            };
        } catch (error) {
            return null;
        }
    }

    /**
     * Get analytics data
     */
    async getAnalytics(dateRange?: { start: Date; end: Date }): Promise<any> {
        const params = new URLSearchParams();
        if (dateRange) {
            params.append('start', dateRange.start.toISOString());
            params.append('end', dateRange.end.toISOString());
        }

        const queryString = params.toString();
        const endpoint = `/analytics${queryString ? `?${queryString}` : ''}`;

        return await this.makeRequest(endpoint);
    }

    /**
     * Close database connection (no-op for API client)
     */
    async close(): Promise<void> {
        // No-op for API client
    }

    private async handleLocalStorageRequest(endpoint: string, options: RequestInit = {}): Promise<any> {
        const method = options.method || 'GET';
        const body = options.body ? JSON.parse(options.body as string) : null;

        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 100));

        if (endpoint.includes('/customers')) {
            if (method === 'POST') {
                return this.saveCustomerToLocalStorage(body);
            } else if (method === 'GET') {
                return this.getCustomerFromLocalStorage(endpoint);
            }
        }

        if (endpoint.includes('/leads') && method === 'POST') {
            return this.saveLeadToLocalStorage(body);
        }

        if (endpoint.includes('/activities') && method === 'POST') {
            return this.saveActivityToLocalStorage(body);
        }

        if (endpoint.includes('/bookings')) {
            if (method === 'POST') {
                return this.saveBookingToLocalStorage(body);
            } else if (method === 'GET') {
                return this.getBookingFromLocalStorage(endpoint);
            }
        }

        return { success: true };
    }

    private saveCustomerToLocalStorage(customer: Customer): Customer {
        const customers = JSON.parse(localStorage.getItem('eclipse_customers') || '[]');
        const existingIndex = customers.findIndex((c: Customer) => c.email === customer.email);

        const customerData = {
            ...customer,
            id: customer.id || Date.now().toString(), // Generate numeric string ID
            createdAt: customer.createdAt || new Date(),
            updatedAt: new Date(),
        };

        if (existingIndex >= 0) {
            customers[existingIndex] = customerData;
        } else {
            customers.push(customerData);
        }

        localStorage.setItem('eclipse_customers', JSON.stringify(customers));
        return customerData;
    }

    private getCustomerFromLocalStorage(endpoint: string): Customer | null {
        const customers = JSON.parse(localStorage.getItem('eclipse_customers') || '[]');
        const emailMatch = endpoint.match(/email=([^&]+)/);

        if (emailMatch) {
            const email = decodeURIComponent(emailMatch[1]);
            return customers.find((c: Customer) => c.email === email) || null;
        }

        return null;
    }

    private saveLeadToLocalStorage(lead: Lead): Lead {
        const leads = JSON.parse(localStorage.getItem('eclipse_leads') || '[]');
        const leadData = {
            ...lead,
            id: lead.id || Date.now().toString(),
            createdAt: lead.createdAt || new Date(),
            updatedAt: new Date(),
        };

        leads.push(leadData);
        localStorage.setItem('eclipse_leads', JSON.stringify(leads));
        return leadData;
    }

    private saveActivityToLocalStorage(activity: UserActivity): UserActivity {
        const activities = JSON.parse(localStorage.getItem('eclipse_activities') || '[]');
        const activityData = {
            ...activity,
            id: activity.id || Date.now().toString(),
            timestamp: activity.timestamp || new Date(),
        };

        activities.push(activityData);
        localStorage.setItem('eclipse_activities', JSON.stringify(activities));
        return activityData;
    }

    private saveBookingToLocalStorage(booking: Booking): Booking {
        const bookings = JSON.parse(localStorage.getItem('eclipse_bookings') || '[]');
        const bookingData = {
            ...booking,
            id: booking.id || Date.now().toString(),
            createdAt: booking.createdAt || new Date(),
            updatedAt: new Date(),
        };

        bookings.push(bookingData);
        localStorage.setItem('eclipse_bookings', JSON.stringify(bookings));
        return bookingData;
    }

    private getBookingFromLocalStorage(endpoint: string): Booking | null {
        const bookings = JSON.parse(localStorage.getItem('eclipse_bookings') || '[]');
        const idMatch = endpoint.match(/\/bookings\/([^?]+)/);

        if (idMatch) {
            const id = idMatch[1];
            return bookings.find((b: Booking) => b.id === id) || null;
        }

        return null;
    }
}

// Export singleton instance
export const databaseService = new DatabaseService();
